import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import {
	Checkbox,
	CollapsibleBlock,
	GroupSelectItemInterface,
	IconButton,
	Icons,
	SearchInput,
	SubmissionButtons,
} from '@components/common';

import { isEmpty } from '@utils/common';

import { TTechnologyNameTitle } from '@interfaces/common/common';
import { IGroupSelectMenuProps } from './GroupSelectMenu.types';
import {
	IGroupSelectCategoryGroup,
	IGroupSelectOptions,
	IGroupSelectOptionsKpi,
} from '@components/common/GroupSelectKpi/GroupSelect.types';

import { UNKNOWN_TITLE } from '@components/common/GroupSelect/constants';

import {
	filterHiddenOptions,
	generateOptions,
	generateOptionsByIds,
	sortByChecked,
	sortByName,
} from '../groupSelectUtils';

import './GroupSelectMenu.scss';

const ITEM_MAX_WIDTH = 465;

const GroupSelectMenu: React.FC<IGroupSelectMenuProps> = ({
	defaultOptions,
	dynamicOptions,
	placeholder,
	searchThreshold,
	onSave,
	toggleSelectMenu,
	isAllItemsSelected,
	selectAllName,
	iSortByChecked,
	withVirtualization,
	readOnly,
	maxSelectionNumber,
	maxSelectionText,
}) => {
	const listRef = useRef();

	const [isCheckedSort, setIsCheckedSort] = useState(iSortByChecked);
	const [isAllItemsInMenuSelected, setIsAllItemsInMenuSelected] = useState(isAllItemsSelected);
	const [itemsCheckedCount, setItemsCheckedCount] = useState(0);

	const [dynamicMenuOptions, setDynamicMenuOptions] = useState(sortByChecked(dynamicOptions));
	const [searchValue, setSearchValue] = useState('');

	const dynamicMenuOptionsForVirtualization = useMemo(() => {
		return filterHiddenOptions(dynamicMenuOptions);
	}, [dynamicMenuOptions]);

	useEffect(() => {
		const allChecked = dynamicMenuOptions.every((option: IGroupSelectOptions) => {
			return option.categoryGroups.every((categoryGroup: IGroupSelectCategoryGroup) => {
				return categoryGroup.items.every((item: GroupSelectItemInterface) => item.checked);
			});
		});

		const itemsCheckedCount: number = dynamicMenuOptions.reduce((acc, option: IGroupSelectOptions) => {
			option.categoryGroups.forEach((categoryGroup: IGroupSelectCategoryGroup) => {
				categoryGroup.items.forEach((item: GroupSelectItemInterface) => {
					if (item.checked) acc++;
				});
			});

			return acc;
		}, 0);

		setItemsCheckedCount(itemsCheckedCount);
		setIsAllItemsInMenuSelected(allChecked);
	}, [dynamicOptions, dynamicMenuOptions]);

	const onChangeSort = () => {
		setIsCheckedSort(prevState => {
			if (!prevState) {
				setDynamicMenuOptions(prevState => sortByChecked(prevState));
			} else setDynamicMenuOptions(prevState => sortByName(prevState));

			return !prevState;
		});
	};

	const refreshOptions = () => setDynamicMenuOptions(prevState => sortByChecked(prevState));

	const getCollapsibleBlockState: () => Record<string, boolean> = (): Record<string, boolean> => {
		return defaultOptions.reduce<Record<string, boolean>>((
			acc,
			curr: IGroupSelectOptions,
			index: number,
		) => {
			curr.categoryGroups.forEach((categoryGroup: IGroupSelectCategoryGroup) => {
				acc[categoryGroup.category.name] = index === 0;
			});

			return acc;
		}, {});
	};

	const [collapsibleBlocksState, setCollapsibleBlocksState] = useState<Record<string, boolean>>(
		() => getCollapsibleBlockState());

	const toggleCollapsibleBlocks = (name: string) => {
		setCollapsibleBlocksState(prevState => {
			return ({
				...prevState,
				[name]: !prevState[name],
			});
		});
	};

	const onChangeHandler = (
		isChecked: boolean,
		ids: Array<string>,
		categoriesNames: string[],
		technology: TTechnologyNameTitle,
	) => {
		setDynamicMenuOptions(generateOptionsByIds(dynamicMenuOptions, !isChecked, ids, categoriesNames, technology));
	};

	const onSearchHandler = useCallback((value: string) => {
		const isEmptyValue = !value;
		const lowercaseValue = value.toLowerCase();

		setSearchValue(value);

		setDynamicMenuOptions((): IGroupSelectOptionsKpi[] => {
			const filteredOptions = dynamicOptions.map((option: IGroupSelectOptions) => {
				const filteredCategoryGroups = option.categoryGroups
					.map((categoryGroup: IGroupSelectCategoryGroup) => {
						const filteredItems = categoryGroup.items.filter((item: GroupSelectItemInterface) => {
							if (isEmptyValue) return true;

							return item?.title?.toLowerCase().includes(lowercaseValue);
						});

						return {
							...categoryGroup,
							items: filteredItems,
						};
					});

				return {
					...option,
					categoryGroups: filteredCategoryGroups,
				};
			});

			if (isEmptyValue) return sortByChecked(filteredOptions);

			return filteredOptions;
		});
	}, [dynamicOptions]);

	useEffect(() => {
		onSearchHandler('');
	}, [onSearchHandler]);

	const toggleAllCheckbox = () => {
		setDynamicMenuOptions(generateOptions(dynamicMenuOptions, !isAllItemsInMenuSelected));
	};

	const onResetHandler = () => {
		setDynamicMenuOptions(dynamicOptions);
		onSearchHandler('');
	};

	const onSaveHandler = () => {
		onSave(dynamicMenuOptions);
		toggleSelectMenu();
	};

	const onSearchClear = () => {
		if (!isCheckedSort) return;

		refreshOptions();
	};

	const repaintAllList = () => {
		if (listRef?.current) {
			dynamicMenuOptionsForVirtualization.forEach((_: any, index: number) => {
				(listRef.current as any)?.resetAfterIndex(index, true);
			});
		}
	};

	useEffect(() => {
		if (withVirtualization) repaintAllList();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dynamicMenuOptions]);

	const isUncheckedDisabled = !!maxSelectionNumber && itemsCheckedCount >= maxSelectionNumber;

	const isMaxSelectionError = !!maxSelectionNumber && itemsCheckedCount > maxSelectionNumber;

	const onChangeItem = (
		item: GroupSelectItemInterface,
		categoryGroup: IGroupSelectCategoryGroup,
		technology: TTechnologyNameTitle,
	) => () => {
		if (!item.id) return;

		onChangeHandler(!!item.checked, [item.id], [categoryGroup.category.name], technology);
	};

	const onChangeCategory = (
		categoryGroup: IGroupSelectCategoryGroup,
		technology: TTechnologyNameTitle,
	) => () => {
		const ids = categoryGroup.items.map(item => item.id);

		onChangeHandler(
			categoryGroup.items[0].checked as boolean,
			ids,
			[categoryGroup.category.name],
			technology,
		);
	};

	const onChangeTechnology = (technology: TTechnologyNameTitle) => () => {
		const option = dynamicMenuOptions
			?.find(option => option.technology.name == technology.name);
		const ids = option?.categoryGroups
			.map(categoryGroup => categoryGroup.items
				.map(item => item.id)).flat();
		const categoriesNames = option?.categoryGroups.map(categoryGroup => categoryGroup.category.name);
		const isTechChecked = option?.categoryGroups.every(categoryGroup =>
			categoryGroup.items.every(item => item.checked));

		onChangeHandler(isTechChecked as boolean, ids!, categoriesNames!, technology);
	};

	return (
		<div
			className='f5gGroupSelectMenuContainer'
			data-testid='group-select-menu'
		>
			<div className='f5gGroupSelectMenuSearch'>
				<div className={cn({ 'f5gGroupMenuSearchHeader': maxSelectionNumber && maxSelectionText })}>
					{maxSelectionNumber && maxSelectionText
						&&
						<div className={cn('f5gGroupMenuMaxSelectionText', { 'f5gGroupMenuMaxSelectionTextError': isMaxSelectionError })}>
							{maxSelectionText}
						</div>
					}
					<div className='f5gGroupSortButtons'>
						<IconButton
							icon={Icons.resetIcon}
							isDisabled={!isCheckedSort || readOnly}
							isFillPath
							isRound
							isWithoutShadow
							onClick={refreshOptions}
							tooltipLabel='Refresh'
						/>
						<IconButton
							icon={Icons.alphabeticSortIcon}
							isActive={!isCheckedSort}
							isRound
							isWithoutShadow
							onClick={onChangeSort}
							tooltipLabel={isCheckedSort ? 'Sort alphabetically' : 'Set default sorting'}
						/>
					</div>
				</div>
				<SearchInput
					isClearable
					onClear={onSearchClear}
					onSearch={onSearchHandler}
					placeholder={placeholder}
					searchThreshold={searchThreshold}
					value={searchValue}
				/>
			</div>
			<div className={cn('f5gGroupSelectMenuOptions', 'mb-15', {
				'withoutVirtualization': !withVirtualization,
			})}>
				{!maxSelectionNumber
					&& <div className={cn('f5gGroupSelectCheckboxOption')}>
						<Checkbox
							checked={isAllItemsInMenuSelected}
							disabled={readOnly}
							isPropagation
							label='All'
							name={selectAllName}
							onChange={toggleAllCheckbox}
						/>
					</div>
				}
				{
					dynamicMenuOptions.filter((option: IGroupSelectOptions) => {
						return !option.categoryGroups.every(categoryGroup => isEmpty(categoryGroup.items));
					}).map((option: IGroupSelectOptions, ind) => {
						const toggleCollapsibleBlock = () => toggleCollapsibleBlocks(option.technology.name);
						const isTechChecked = option.categoryGroups.every(categoryGroup =>
							categoryGroup.items.every(item => item.checked));

						const isIndefinite = option.categoryGroups.some(categoryGroup =>
							categoryGroup.items.some(item => item.checked));

						return (
							<CollapsibleBlock
								key={ind + option.technology.name}
								isOpen={!collapsibleBlocksState[option.technology.name]}
								label={
									<div className={cn('f5gGroupSelectCheckboxOption', 'mb-15')}>
										<Checkbox
											checked={isTechChecked}
											disabled={readOnly}
											isIndefinite={isIndefinite && !isTechChecked}
											isOverflow={false}
											label={option.technology.title}
											maxWidthOverflow={ITEM_MAX_WIDTH}
											name={option.technology.title}
											onChange={onChangeTechnology(option.technology)}
											readOnly={false}
										/>
									</div>
								}
								name='Technologies'
								toggleState={toggleCollapsibleBlock}
								withSeparatorLine={false}
							>
								{
									option.categoryGroups.map((categoryGroup: IGroupSelectCategoryGroup, i) => {
										const technology = option.technology;
										const path = `${option.technology.name}/${categoryGroup.category.name}`;
										const toggleCollapsibleBlock = () => toggleCollapsibleBlocks(path);

										const isEmptyCategory = isEmpty(categoryGroup.items);
										const isCategoryChecked = !isEmptyCategory
											&& categoryGroup.items.every(item => item.checked);

										const isIndefinite = categoryGroup.items.some(item => item.checked);

										return (!isEmptyCategory
											&& <CollapsibleBlock
												key={i + option.technology.name + categoryGroup.category.name}
												classNames='ml-25'
												isOpen={!collapsibleBlocksState[path]}
												label={
													<div className={cn('f5gGroupSelectCheckboxOption', 'mb-15')}>
														<Checkbox
															checked={isCategoryChecked}
															disabled={readOnly}
															isIndefinite={isIndefinite && !isCategoryChecked}
															isOverflow={false}
															label={categoryGroup.category.title}
															maxWidthOverflow={ITEM_MAX_WIDTH}
															name={option.technology.name + categoryGroup.category.name}
															onChange={onChangeCategory(categoryGroup, technology)}
															readOnly={false}
														/>
													</div>
												}
												name='Categories'
												toggleState={toggleCollapsibleBlock}
												withSeparatorLine={false}
											>
												{
													categoryGroup.items.map((item: GroupSelectItemInterface) => {
														const label: string = item.isAbsent
															? `${item.title || 'Unknown Title'} (absent)`
															: item.title || 'Unknown Title';

														return (
															<div
																key={item.id}
																className={cn(
																	'f5gGroupSelectCheckboxOption',
																	'ml-25',
																	'mb-10',
																	{
																		'f5gGroupSelectMenuItemCheckboxHidden': item.isHidden,
																	},
																)}
															>
																<Checkbox
																	checked={item.checked}
																	disabled={readOnly}
																	isOverflow={true}
																	isUncheckedDisabled={isUncheckedDisabled}
																	label={label}
																	maxWidthOverflow={ITEM_MAX_WIDTH}
																	name={item.title || UNKNOWN_TITLE}
																	onChange={
																		onChangeItem(item, categoryGroup, technology)
																	}
																	readOnly={readOnly}
																	tags={item.tags}
																/>
															</div>
														);
													})
												}
											</CollapsibleBlock>
										);
									})
								}
							</CollapsibleBlock>
						);
					})
				}
			</div>
			{!readOnly && (
				<div className='f5gGroupSelectMenuButtons'>
					<SubmissionButtons
						disabledPositive={isMaxSelectionError}
						negativeHandler={onResetHandler}
						negativeLabel='Revert changes'
						position='right'
						positiveHandler={onSaveHandler}
						positiveLabel='Save'
					/>
				</div>
			)}
		</div>
	);
};

export default GroupSelectMenu;
